import { FormHelperText, Grid, Typography } from '@mui/material';
import { MESSAGE_FETCH_ERROR } from '@/utils/validation-utils';
import { MaskInput } from '@/components/common/field/MaskInput';
import { PhoneErrorInfo } from '@/types/PhoneErrorInfo';
import {
  SMS_MESSAGE_ATTEMPTS_EXHAUSTED,
  SMS_MESSAGE_CODE_EMPTY,
  SMS_MESSAGE_CODE_EXPIRED,
  SMS_MESSAGE_CODE_SENT_PREFIX,
  SMS_MESSAGE_PHONE_ALREADY_BOUND,
  SMS_MESSAGE_RESEND_CODE,
  SMS_RESEND_COOLDOWN_SECONDS,
  extractPhoneError,
  formatResendBlockMessage,
  formatResendCountdownMessage,
  formatWrongCodeMessage,
  maskRussianPhone,
  resolvePhoneErrorKind,
} from '@/utils/phone-utils';
import { Theme } from '@mui/material/styles';
import { TypographyLink } from '@/components/common/TypographyLink';
import { onlyNumberFormat } from '@/utils/string-utils';
import {
  useAddNewPhoneMutation,
  useConfirmNewPhoneMutation,
  useGetSmsSettingsQuery,
  useSetNewPhoneMutation,
} from '@/services/api/settingsApiSlice';
import { useSnackbar } from 'notistack';
import LoadingOutlinedField from '@/components/common/field/LoadingOutlinedField';
import React, { ChangeEvent, FC, KeyboardEvent, RefObject, useEffect, useMemo, useState } from 'react';
import type { PhoneErrorKind } from '@/utils/phone-utils';

/**
 * Length of a fully entered SMS verification code.
 */
const CODE_LENGTH = 4;

/**
 * Digits-only mask for the code field, in the project mask syntax where `9` marks a digit slot.
 * The mask engine rejects a fifth character outright, which both caps the length
 * and keeps the field digits-only without a separate check.
 */
const CODE_MASK = '9'.repeat(CODE_LENGTH);

/**
 * Slot marker for the code mask, shown for every digit the user has not entered yet.
 * It must not be a digit, otherwise the mask engine would treat it as entered data.
 */
const CODE_MASK_PLACEHOLDER = '*';

/**
 * Height of the code input in pixels, used to centre the resend control against the field itself
 * rather than against the column, whose height changes with the helper text.
 */
const CODE_FIELD_HEIGHT = 48;

interface Props {
  newPhone: string;
  isAddPhone?: boolean;
  isMobile?: boolean;
  setSuccessChange: (isSuccessChange: boolean) => void;
  submitButtonRef: RefObject<HTMLButtonElement | null>;
  onLoading?: (isLoading: boolean) => void;
  initialCooldownSeconds?: number;
  initialBlockSeconds?: number;
}

export const PhoneCodeForm: FC<Props> = (props: Props) => {
  const {
    newPhone,
    isAddPhone,
    setSuccessChange,
    submitButtonRef,
    onLoading,
    initialCooldownSeconds,
    initialBlockSeconds,
  } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [confirmPhone, { isLoading: isConfirming }] = useConfirmNewPhoneMutation();
  const [setNewPhone, { isLoading: isChangingPhone }] = useSetNewPhoneMutation();
  const [addNewPhone, { isLoading: isAddingPhone }] = useAddNewPhoneMutation();

  // The backend configuration is the single source of truth for the verification timings;
  // the constant below is only a fallback for the window before the request resolves,
  // used to seed a countdown this component starts itself.
  // A duration the backend reports is taken as given,
  // since it knows the configured policy and this component may not yet.
  const { data: smsSettings } = useGetSmsSettingsQuery();
  const resendCooldown: number = smsSettings?.resendCooldownSeconds ?? SMS_RESEND_COOLDOWN_SECONDS;

  // The resend endpoint mirrors the phone-entry step that originally sent the code.
  const resendPhone = isAddPhone ? addNewPhone : setNewPhone;
  const isResending: boolean = isChangingPhone || isAddingPhone;

  const [code, setCode] = useState<string>('');
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [errorKind, setErrorKind] = useState<PhoneErrorKind>('unknown');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);
  // The last code the backend rejected as wrong, so an unchanged resubmit cannot spend another attempt.
  const [rejectedCode, setRejectedCode] = useState<string | undefined>(undefined);

  // A code was just sent on the previous step, so the resend cooldown starts active.
  // When the previous step was rejected by an active cooldown or block,
  // it hands over the remaining seconds reported by the backend, which take precedence over the configured duration.
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(initialCooldownSeconds ?? SMS_RESEND_COOLDOWN_SECONDS);
  const [blockSeconds, setBlockSeconds] = useState<number>(initialBlockSeconds ?? 0);

  const hasCountdown: boolean = cooldownSeconds > 0 || blockSeconds > 0;

  useEffect(() => {
    if (!hasCountdown) {
      return;
    }
    const timer = setInterval(() => {
      setCooldownSeconds((s) => Math.max(0, s - 1));
      setBlockSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return (): void => clearInterval(timer);
  }, [hasCountdown]);

  // The initial countdown starts from the fallback so the field is usable immediately;
  // snap it to the configured value once the settings arrive.
  // The dependency is the fetched number rather than the response object,
  // so a refetch returning the same value does not restart the timer.
  // A cooldown carried over from the previous step is already the real remaining time,
  // so it must not be overwritten by the full configured duration.
  const fetchedCooldown: number | undefined = smsSettings?.resendCooldownSeconds;
  useEffect(() => {
    if (fetchedCooldown != null && initialCooldownSeconds == null) {
      setCooldownSeconds(fetchedCooldown);
    }
  }, [fetchedCooldown, initialCooldownSeconds]);

  // The dialog owns the submit button, so it needs the confirmation request state.
  useEffect(() => {
    if (onLoading) {
      onLoading(isConfirming);
    }
  }, [isConfirming, onLoading]);

  const fieldErrorMessage: string = useMemo(() => {
    switch (errorKind) {
      case 'wrong':
        return formatWrongCodeMessage(remainingAttempts);
      case 'empty':
        return SMS_MESSAGE_CODE_EMPTY;
      case 'exhausted':
        return SMS_MESSAGE_ATTEMPTS_EXHAUSTED;
      case 'expired':
        return SMS_MESSAGE_CODE_EXPIRED;
      case 'alreadyBound':
        return SMS_MESSAGE_PHONE_ALREADY_BOUND;
      default:
        return '';
    }
  }, [errorKind, remainingAttempts]);

  const confirmCode = async (digits: string): Promise<void> => {
    // A code the backend has already rejected cannot start matching,
    // so resubmitting it unchanged would spend another attempt on a request certain to fail.
    // Editing clears the message, so it is restored here rather than left absent —
    // otherwise retyping the rejected code would look accepted.
    if (digits === rejectedCode) {
      setErrorKind('wrong');
      return;
    }
    // The fourth digit submits on its own, so an Enter arriving a moment later would send the same code a second time;
    // whichever response lands last would be judging a code the other has already consumed.
    if (isConfirming) {
      return;
    }
    setErrorKind('unknown');
    try {
      await confirmPhone({ phone: newPhone, code: parseInt(digits, 10) }).unwrap();
      setConfirmed(true);
      setSuccessChange(true);
    } catch (e: unknown) {
      const phoneError: PhoneErrorInfo | undefined = extractPhoneError(e);
      const kind: PhoneErrorKind = resolvePhoneErrorKind(phoneError?.code);
      setErrorKind(kind);
      if (kind === 'wrong') {
        setRejectedCode(digits);
      }
      if (phoneError?.remainingAttempts != null) {
        setRemainingAttempts(phoneError.remainingAttempts);
      }
      if (phoneError?.secondsRemaining != null) {
        if (kind === 'block') {
          setBlockSeconds(phoneError.secondsRemaining);
        } else if (kind === 'cooldown') {
          setCooldownSeconds(phoneError.secondsRemaining);
        }
      }
      if (kind === 'unknown') {
        enqueueSnackbar(phoneError?.message || MESSAGE_FETCH_ERROR, { variant: 'error' });
      }
    }
  };

  const handleCode = async (value: string): Promise<void> => {
    const digits: string = onlyNumberFormat(value);
    setCode(digits);
    if (errorKind === 'empty' || errorKind === 'wrong') {
      setErrorKind('unknown');
    }
    if (digits.length === CODE_LENGTH) {
      await confirmCode(digits);
    }
  };

  /**
   * Submits the entered code.
   * An incomplete code cannot match the issued one, so it is rejected locally rather than spending
   * one of the user's verification attempts on a request that is certain to fail.
   * The mask caps the field at `CODE_LENGTH`, so in practice this guards the empty and partially entered cases,
   * but the check is written against the exact length to stay correct regardless of the mask.
   * The backend keeps its own `CODE_EMPTY` guard for callers that bypass the UI.
   */
  const handleSubmitCode = async (): Promise<void> => {
    if (code.length !== CODE_LENGTH) {
      setErrorKind('empty');
      return;
    }
    await confirmCode(code);
  };

  const handleResend = async (): Promise<void> => {
    if (cooldownSeconds > 0 || blockSeconds > 0 || isResending) {
      return;
    }
    try {
      await resendPhone({ phone: newPhone }).unwrap();
      setCode('');
      setErrorKind('unknown');
      setRemainingAttempts(undefined);
      // A new code was issued, so the previously rejected one is no longer the code under test.
      setRejectedCode(undefined);
      setCooldownSeconds(resendCooldown);
    } catch (e: unknown) {
      const phoneError: PhoneErrorInfo | undefined = extractPhoneError(e);
      const kind: PhoneErrorKind = resolvePhoneErrorKind(phoneError?.code);
      if (kind === 'cooldown' && phoneError?.secondsRemaining != null) {
        setCooldownSeconds(phoneError.secondsRemaining);
      } else if (kind === 'block' && phoneError?.secondsRemaining != null) {
        setBlockSeconds(phoneError.secondsRemaining);
      } else if (kind === 'unknown') {
        enqueueSnackbar(phoneError?.message || MESSAGE_FETCH_ERROR, { variant: 'error' });
      }
    }
  };

  const blockActive: boolean = blockSeconds > 0;
  const isResendAllowed: boolean = !blockActive && cooldownSeconds === 0 && !isResending;

  const resendLabel: string = blockActive
    ? formatResendBlockMessage(blockSeconds)
    : cooldownSeconds > 0
      ? formatResendCountdownMessage(cooldownSeconds)
      : SMS_MESSAGE_RESEND_CODE;

  return (
    <Grid container={true} direction={'column'}>
      <Grid item={true} mb={2}>
        <Typography variant={'body1'} lineHeight={'20px'}>
          {`${SMS_MESSAGE_CODE_SENT_PREFIX}\u00A0`}
          <Typography component={'span'} variant={'inherit'} sx={{ whiteSpace: 'nowrap' }}>
            {maskRussianPhone(newPhone)}
          </Typography>
        </Typography>
      </Grid>
      <Grid item={true}>
        <Grid container={true} spacing={2} alignItems={'center'}>
          <Grid item={true} xs={6} sm={4}>
            <LoadingOutlinedField
              fullWidth={true}
              name={'code'}
              value={code}
              autoFocus={true}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleCode(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key !== 'Enter') {
                  return;
                }
                e.preventDefault();
                void handleSubmitCode();
              }}
              error={Boolean(fieldErrorMessage)}
              aria-describedby={'code-input-helperText'}
              isLoading={isConfirming}
              isSuccess={confirmed}
              inputComponent={MaskInput as any} // eslint-disable-line @typescript-eslint/no-explicit-any
              inputProps={{ mask: CODE_MASK, maskPlaceholder: CODE_MASK_PLACEHOLDER, alwaysShowMask: true }}
            />
          </Grid>
          <Grid item={true} xs={6} sm={true}>
            {isResendAllowed ? (
              <Typography variant={'body2'} component={'div'}>
                <TypographyLink onClick={handleResend} aria-live={'polite'} data-testid={'resend-code'}>
                  {resendLabel}
                </TypographyLink>
              </Typography>
            ) : (
              <Typography
                variant={'body2'}
                data-testid={'resend-code'}
                sx={{
                  color: (theme: Theme) => theme.colors.grayText,
                  minHeight: { sm: `${CODE_FIELD_HEIGHT}px` },
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'pre-line',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                {resendLabel}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item={true}>
        <FormHelperText error={Boolean(fieldErrorMessage)} id={'code-input-helperText'}>
          {fieldErrorMessage || ' '}
        </FormHelperText>
      </Grid>
      <button onClick={handleSubmitCode} hidden={true} ref={submitButtonRef}>
        {'submit'}
      </button>
    </Grid>
  );
};
